import { useState, useEffect, useMemo } from 'react';
import services from 'services';

/**
 * Custom hook for loading and managing available SystemZero models data
 * Shared between user and admin settings pages
 * @returns {Object} Model data and loading state
 */
export const useSystemZeroModels = () => {
  const [availableModels, setAvailableModels] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Load available models
  const loadAvailableModels = async () => {
    try {
      setIsLoading(true);
      const response = await services.getAvailableModels();
      const modelMap = response.data;

      // Convert map of id -> displayName to objects, sort by display name, add default option first
      const sortedModels = Object.entries(modelMap)
        .map(([id, displayName]) => ({
          id: id,
          name: displayName,
          isDefault: false
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      const models = [{ id: 'Default', name: 'Default', isDefault: true }, ...sortedModels];
      setAvailableModels(models);
    } catch (error) {
      console.error('Error loading available models:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Model options for Select component
  const modelOptions = useMemo(
    () =>
      availableModels?.map(model => ({
        label: model.name,
        value: model.id
      })) || [],
    [availableModels]
  );

  // Get current display value helper
  const getCurrentValue = currentValue => {
    return currentValue || 'Default';
  };

  // Load models on mount
  useEffect(() => {
    loadAvailableModels();
  }, []);

  return {
    availableModels,
    modelOptions,
    isLoading,
    getCurrentValue,
    loadAvailableModels
  };
};
